<script setup lang="ts">
	import PageHeader from "@/components/Navigation/PageHeader.vue";
	import { TextInput, LoadingSkeleton } from "@ptx-autonomous/libs/ui";
	import CopyTextButton from "@/components/CopyTextButton.vue";
	import {
		usePortalServicesHttp,
		usePostMachinePassword,
	} from "@/composables/portal-services-api";
	import { reactive, ref } from "vue";
	import { z } from "zod";
	import { useValidation } from "@ptx-autonomous/libs/forms";

	const portalServicesHttp = usePortalServicesHttp();

	const postMachinePassword = usePostMachinePassword(portalServicesHttp);
	const searchedSerialNumber = ref("");
	const password = ref("");
	const error = ref("");
	async function submit() {
		password.value = "";
		searchedSerialNumber.value = result.serialNumber.trim();
		if (!validation.validate()) {
			password.value = "";
			return;
		}
		try {
			const response = await postMachinePassword.mutateAsync({
				serialNumber: searchedSerialNumber.value,
			});

			if (response) {
				password.value = response.data;
				result.serialNumber = "";
			} else {
				error.value = "No password found";
			}
		} catch (e: any) {
			password.value = "";
			error.value = e.message;
		}
	}
	const serialNumberSchema = z.object({
		serialNumber: z
			.string()
			.trim()
			.regex(/^\d{6}-[A-Za-z0-9]{6}-\d{6}$/, {
				message:
					"Invalid format: expected 6 digits - 6 alphanumeric - 6 digits",
			}),
	});

	const result = reactive({ serialNumber: "" });
	const validation = useValidation(result, serialNumberSchema);
</script>
<template>
	<div class="box-border flex min-h-0 flex-col gap-4 pl-5">
		<div class="flex">
			<PageHeader title="Machine Password" />
		</div>
		<div
			class="border-light-grey mx-auto flex w-[50rem] flex-col justify-center rounded border bg-netural-orange p-10 shadow-md"
		>
			<TextInput
				label="Serial Number"
				placeholder="Enter Serial Number"
				class="my-4 w-full text-dark-green"
				v-model="result.serialNumber"
				:error="result.serialNumber ? validation.errors.serialNumber : ''"
				@blur="validation.touch.serialNumber"
			/>
			<button
				class="rounded bg-bright-orange p-2 font-bold text-dark-orange disabled:opacity-50"
				@click="submit"
				:disabled="postMachinePassword.isPending.value"
			>
				Submit
			</button>

			<div
				class="mt-8 self-start text-dark-green"
				v-if="password"
			>
				<p>Serial Number: {{ searchedSerialNumber }}</p>
				<CopyTextButton
					:display-text="`Password: ${password} `"
					:text="password"
				/>
			</div>

			<LoadingSkeleton v-if="postMachinePassword.isPending.value" />

			<div
				v-if="error"
				class="text-error mt-8 self-start"
			>
				<p>{{ error }}</p>
			</div>
		</div>
	</div>
</template>
